import { ApplicationsEnum, DictionaryInterface, STORE_WRAPPER_TOKEN, StoreWrapperInterface, UserInterface } from '@actassa/api';
import { Inject, Injectable } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { Dispatch } from '@ngxs-labs/dispatch-decorator';
import { isArray, isString } from 'lodash-es';
import { Observable, Subscription, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, tap } from 'rxjs/operators';

import { DeleteDataCollection } from '../+state/actions/delete-data-collection';
import { GetInspectionsRaw } from '../+state/actions/get-inspections-raw';
import { LoadTemplates } from '../+state/actions/load-templates';
import { SaveDataCollection } from '../+state/actions/save-data-collection';
import { DataCollectionInterface } from '../interfaces/data-collection.interface';
import { InspectionListItemInterface } from '../interfaces/inspection-list-item.interface';
import { TemplateInterface } from '../interfaces/template.interface';
import { DataCollectionType } from '../types/inspection-type';

import { DataCollectionTemplatesStoreService } from './data-collection-templates-store.service';
import { DataCollectionsStoreService } from './data-collections-store.service';
import { InspectionsService } from './inspections.service';
import { LoaderService } from './loader.service';
import { TemplatesService } from './templates.service';
import { UpdateDataCollection } from '../+state/actions/update-data-collection';

@Injectable({
    providedIn: 'root',
})
export class DataCollectionFacadeService {
    public baseUrl$ = this.mainStore.baseUrl$;
    public loadingState$ = this.loaderService.state$;
    public showToast = this.mainStore.showToast;
    public showAlert = this.mainStore.showAlert;
    public user: UserInterface;
    public user$ = this.mainStore.user$;

    private subscriptions: Subscription;

    constructor(
        @Inject(STORE_WRAPPER_TOKEN) private readonly mainStore: StoreWrapperInterface,
        private readonly dataCollectionsStoreService: DataCollectionsStoreService,
        private readonly dataCollectionTemplatesStoreService: DataCollectionTemplatesStoreService,
        private readonly inspectionsService: InspectionsService,
        private readonly loaderService: LoaderService,
        private readonly templatesService: TemplatesService,
    ) {
        this.isActionSubscriptionEnabled$
            .pipe(
                distinctUntilChanged(),
                tap((isAuthenticated: boolean) => {
                    if (isAuthenticated) {
                        const inspectionSubscription = this.inspectionsService.init$().subscribe();
                        const templateSubscription = this.templatesService.init$().subscribe();
                        const userSubscription = this.mainStore.user$.subscribe((user: UserInterface) => this.user = user);

                        this.subscriptions = new Subscription();
                        this.subscriptions.add(inspectionSubscription);
                        this.subscriptions.add(templateSubscription);
                        this.subscriptions.add(userSubscription);

                        setTimeout(() => this.preloadData(), 1000);

                        return;
                    }

                    this.subscriptions?.unsubscribe();
                }),
            )
            .subscribe();
    }

    public activeInspectionList$(types: Array<DataCollectionType>): Observable<Array<InspectionListItemInterface>> {
        return this.dataCollectionsStoreService.getDataCollections$(types)
            .pipe(
                map((dataCollections: Array<InspectionListItemInterface>) => dataCollections
                    .filter(dataCollection => !dataCollection.isFinished)),
            );
    }

    public getDataCollections$(types: Array<DataCollectionType>): Observable<Array<InspectionListItemInterface>> {
        return this.dataCollectionsStoreService.getDataCollections$(types);
    }

    public getDataCollection$(id: string): Observable<DataCollectionInterface> {
        return this.dataCollectionsStoreService.getDataCollection$(id);
    }

    public activate(): void {
        this.dataCollectionsStoreService.activate();
    }

    public getActiveDataCollectionByTemplateId$(templateId: string): Observable<DataCollectionInterface | undefined> {
        return this.dataCollectionsStoreService.getActiveDataCollectionByTemplateId$(templateId);
    }

    public getDataCollectionTemplate$(templateId: string): Observable<TemplateInterface> {
        return this.dataCollectionTemplatesStoreService.getDataCollectionTemplate$(templateId);
    }

    public buildDataCollectionFromTemplate(template: TemplateInterface): DataCollectionInterface {
        return this.inspectionsService.mapTemplateInspection(template, this.user);
    }

    public buildDataCollectionForm(dataCollection: DataCollectionInterface): FormGroup {
        return this.inspectionsService.createFormGroup(dataCollection);
    }

    public prepareDataCollectionForSave(
        dataCollection: DataCollectionInterface,
        form: FormGroup,
        isFinished?: boolean,
        isDeleted?: boolean,
    ): DataCollectionInterface {
        return this.inspectionsService.prepare(dataCollection, form, isFinished, isDeleted);
    }

    public handlePostFinishActions(dataCollection: DataCollectionInterface): void {
        return this.inspectionsService.handlePostFinishActions(dataCollection);
    }

    public loadingStart(): void {
        this.loaderService.loadingStart();
    }

    public loadingEnd(): void {
        this.loaderService.loadingEnd();
    }

    // The same method in the orchestrator
    public preFillDataCollection(
        dataCollection: DataCollectionInterface,
        preFilledAnswers?: DictionaryInterface<string | number>,
    ): DataCollectionInterface {
        if (!preFilledAnswers) {
            return dataCollection;
        }

        return {
            ...dataCollection,
            questions: dataCollection.questions.map(question => {
                const prefilledAnswer = preFilledAnswers[question.key];

                if (prefilledAnswer) {
                    if (question.controlType === 'multiselect') {
                        if (isArray(prefilledAnswer)) {
                            return {
                                ...question,
                                value: prefilledAnswer,
                            };
                        }

                        if (isString(prefilledAnswer)) {
                            return {
                                ...question,
                                value: prefilledAnswer.split(',').filter(Boolean),
                            };
                        }

                        return {
                            ...question,
                            value: prefilledAnswer,
                        };
                    }

                    return {
                        ...question,
                        value: prefilledAnswer,
                    };
                }

                return question;
            }),
        };
    }

    public loadTemplates$(): Observable<any> {
        return this.templatesService.load$();
    }

    public batchDataCollectionCreate$(dataCollection: DataCollectionInterface, isFinished: boolean): Observable<unknown> {
        return this.inspectionsService.batchDataCollectionCreate$(dataCollection, isFinished);
    }

    private get isActionSubscriptionEnabled$(): Observable<boolean> {
        const { isAuthenticated$, app$ } = this.mainStore;
        const enabledRoles$ = app$
            .pipe(
                map((app: ApplicationsEnum) => Boolean(app)),
            );

        return combineLatest([isAuthenticated$, enabledRoles$])
            .pipe(
                map((keys: Array<boolean>) => keys.every(Boolean)),
            );
    }

    @Dispatch()
    public createInspection(dataCollection: DataCollectionInterface): SaveDataCollection {
        return new SaveDataCollection(dataCollection);
    }

    @Dispatch()
    public updateInspection(dataCollection: Partial<DataCollectionInterface>): UpdateDataCollection {
        return new UpdateDataCollection(dataCollection);
    }

    @Dispatch()
    public deleteInspection(dataCollection: DataCollectionInterface): DeleteDataCollection {
        return new DeleteDataCollection(dataCollection);
    }

    @Dispatch()
    private preloadData(): Array<GetInspectionsRaw | LoadTemplates> {
        return [new GetInspectionsRaw(), new LoadTemplates()];
    }
}
